import { PrayerTimes } from "../types/PrayerTypes";
import { formatHijriDate } from "./dateUtils";
/**
 * Parse time string (HH:MM format) to Date object
 * Azure-optimized for handling prayer time formats
 */
export function parseTimeToDate(timeStr: string | undefined): Date | null {
  if (!timeStr || timeStr === "--:--") return null;

  const today = new Date();
  const [hourStr, minuteStr] = timeStr.split(":");

  const hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  if (isNaN(hour) || isNaN(minute)) return null;

  const result = new Date(today);
  result.setHours(hour, minute, 0, 0);

  return result;
}

/**
 * Check if current time is within a range of minutes from a base time
 */
export function isTimeInRange(
  baseTime: string | undefined,
  offsetMinutes: number,
  durationMinutes: number,
): boolean {
  if (!baseTime) return false;

  const baseDate = parseTimeToDate(baseTime);
  if (!baseDate) return false;

  const now = new Date();
  const startTime = new Date(baseDate.getTime() + offsetMinutes * 60000);
  const endTime = new Date(startTime.getTime() + durationMinutes * 60000);

  return now >= startTime && now <= endTime;
}

/**
 * Format seconds as a countdown string.
 * Returns an object with hours, minutes, seconds for segmented display,
 * or a formatted string for legacy use.
 */
export function formatCountdown(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  if (mins > 0) {
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }
  return `${secs}`;
}

/**
 * Parse countdown seconds into separate hours, minutes, seconds segments
 */
export function parseCountdownSegments(totalSeconds: number): {
  hours: string;
  minutes: string;
  seconds: string;
} {
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  return {
    hours: hrs.toString().padStart(2, "0"),
    minutes: mins.toString().padStart(2, "0"),
    seconds: secs.toString().padStart(2, "0"),
  };
}

/**
 * Get minutes between two time strings
 */
export function getMinutesBetweenTimes(time1: string, time2: string): number {
  const date1 = parseTimeToDate(time1);
  const date2 = parseTimeToDate(time2);

  if (!date1 || !date2) return 0;

  const diffMs = date2.getTime() - date1.getTime();
  return Math.round(diffMs / 60000);
}

/**
 * Azure Best Practice: Centralized time utility functions for consistent behavior
 * Determines which sunrise time to display based on current time and makrooh settings
 */
export function getSunriseTimeDisplay(
  currentTimeStr: string,
  todaySunrise: string,
  tomorrowSunrise: string | undefined,
  sunriseMakroohPeriod: number,
): { time: string; isTomorrow: boolean } {
  // Default to today if no tomorrow data
  if (!tomorrowSunrise)
    return { time: todaySunrise || "--:--", isTomorrow: false };

  // Convert all times to minutes for reliable numeric comparison
  const currentMinutes = timeStrToMinutes(currentTimeStr);
  const sunriseMinutes = timeStrToMinutes(todaySunrise);

  // Current time is before today's sunrise - show today's
  if (currentMinutes < sunriseMinutes) {
    return { time: todaySunrise, isTomorrow: false };
  }

  // If makrooh period is enabled, check if we're still within it
  if (sunriseMakroohPeriod > 0) {
    const makroohEndMinutes = sunriseMinutes + sunriseMakroohPeriod;

    // Still in makrooh period, show today's sunrise
    if (currentMinutes < makroohEndMinutes) {
      return { time: todaySunrise, isTomorrow: false };
    }
  }

  // Past sunrise and past makrooh period (if enabled) - show tomorrow's
  return { time: tomorrowSunrise, isTomorrow: true };
}

// Reuse existing timeStrToMinutes function if available, or define it:
export function timeStrToMinutes(time: string): number {
  if (!time || time === "--:--") return 0;
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

/**
 * Azure Best Practice: Smart Jummah display logic that handles sequential prayers
 * Determines which Jummah prayer (if any) should be displayed
 * @returns An object containing whether to show Jummah and which specific one (1-4)
 */
export function getJummahDisplayInfo(
  currentDate: Date,
  currentTimeStr: string,
  prayerTimes: PrayerTimes,
  screenData?: any, // Optional screenData for durations
): {
  showJummah: boolean;
  jummahNumber: number;
  jummahTime: string | undefined;
} {
  const dayOfWeek = currentDate.getDay();
  const isFriday = dayOfWeek === 5; // Friday is 5 in JavaScript
  const isThursday = dayOfWeek === 4;

  // Default - don't show any Jummah
  const defaultResult = {
    showJummah: false,
    jummahNumber: 0,
    jummahTime: undefined,
  };

  // Check if at least Jummah1 time exists
  if (!prayerTimes.Jummah1 || prayerTimes.Jummah1 === "--:--") {
    return defaultResult;
  }

  // On Friday, determine which Jummah time to show based on current time
  if (isFriday) {
    const currentMinutes = timeStrToMinutes(currentTimeStr);
    const jummahKeys = ["Jummah1", "Jummah2", "Jummah3", "Jummah4"];

    // First, check if any Jummah is currently in progress
    for (let i = 0; i < jummahKeys.length; i++) {
      const key = jummahKeys[i];
      const jummahTime = (prayerTimes as any)[key];
      if (jummahTime && jummahTime !== "--:--") {
        const jummahStartMinutes = timeStrToMinutes(jummahTime);
        // Use getPrayerLength, but since it's in prayerTimeUtils, and screenData is passed, assume it's available
        const lengthSec =
          key === "Jummah1" ? screenData?.jummahLength || 1800 : 15 * 60;
        const jummahEndMinutes = jummahStartMinutes + Math.ceil(lengthSec / 60);
        if (
          currentMinutes >= jummahStartMinutes &&
          currentMinutes < jummahEndMinutes
        ) {
          return {
            showJummah: true,
            jummahNumber: i + 1,
            jummahTime: jummahTime,
          };
        }
      }
    }

    // No Jummah in progress, find the next one
    for (let i = 0; i < jummahKeys.length; i++) {
      const key = jummahKeys[i];
      const jummahTime = (prayerTimes as any)[key];
      if (jummahTime && jummahTime !== "--:--") {
        const jummahMinutes = timeStrToMinutes(jummahTime);
        if (currentMinutes < jummahMinutes) {
          return {
            showJummah: true,
            jummahNumber: i + 1,
            jummahTime: jummahTime,
          };
        }
      }
    }

    // All Jummah prayers have passed - revert to Dhuhr
    return defaultResult;
  }

  // On Thursday after Dhuhr, show tomorrow's Jummah1
  if (isThursday) {
    const dhuhrJam = prayerTimes.JamDhuhr || prayerTimes.BegDhuhr;
    if (!dhuhrJam) return defaultResult;

    const currentMinutes = timeStrToMinutes(currentTimeStr);
    const dhuhrMinutes = timeStrToMinutes(dhuhrJam);

    if (currentMinutes > dhuhrMinutes) {
      return {
        showJummah: true,
        jummahNumber: 1,
        jummahTime: prayerTimes.Jummah1,
      };
    }
  }

  return defaultResult;
}

/**
 * Azure Best Practice: Centralized time utility function for consistent behavior
 * Determines which Sehri End time to display based on current time
 */
export function getSehriTimeDisplay(
  currentTimeStr: string,
  todaySehriEnd: string,
  tomorrowSehriEnd: string | undefined,
): { time: string; isTomorrow: boolean } {
  // Default to today if no tomorrow data
  if (!tomorrowSehriEnd)
    return { time: todaySehriEnd || "--:--", isTomorrow: false };

  // Convert all times to minutes for reliable numeric comparison
  const currentMinutes = timeStrToMinutes(currentTimeStr);
  const sehriEndMinutes = timeStrToMinutes(todaySehriEnd);

  // Current time is before today's sehri end - show today's
  if (currentMinutes < sehriEndMinutes) {
    return { time: todaySehriEnd, isTomorrow: false };
  }

  // Past sehri end time - show tomorrow's
  return { time: tomorrowSehriEnd, isTomorrow: true };
}

/**
 * Azure Best Practice: Centralized time utility function for consistent behavior
 * Determines which Noon time to display based on current time
 */
export function getNoonTimeDisplay(
  currentTimeStr: string,
  todayNoon: string,
  tomorrowNoon: string | undefined,
): { time: string; isTomorrow: boolean } {
  // Default to today if no tomorrow data
  if (!tomorrowNoon) return { time: todayNoon || "--:--", isTomorrow: false };

  // Convert all times to minutes for reliable numeric comparison
  const currentMinutes = timeStrToMinutes(currentTimeStr);
  const noonMinutes = timeStrToMinutes(todayNoon);

  // Current time is before today's noon - show today's
  if (currentMinutes < noonMinutes) {
    return { time: todayNoon, isTomorrow: false };
  }

  // Past noon time - show tomorrow's
  return { time: tomorrowNoon, isTomorrow: true };
}

// Update the formatTimeDisplay function to handle edge cases
export function formatTimeDisplay(
  timeStr: string | null | undefined,
  use12HourFormat: boolean = true,
): string {
  // Handle null/undefined values
  if (!timeStr) return "--:--";

  // Handle already formatted placeholder
  if (timeStr === "--:--") return timeStr;

  try {
    // Check if the string has a valid format we can work with
    if (!timeStr.includes(":")) return timeStr;

    const [hoursStr, minutesStr] = timeStr.split(":");

    // Validate that we have valid numbers
    if (
      !hoursStr ||
      !minutesStr ||
      isNaN(parseInt(hoursStr)) ||
      isNaN(parseInt(minutesStr))
    ) {
      return timeStr; // Return original if not valid time format
    }

    const hours = parseInt(hoursStr);
    const minutes = parseInt(minutesStr);

    // For 12-hour format (with am/pm)
    if (use12HourFormat) {
      const period = hours >= 12 ? "pm" : "am";
      const hours12 = hours % 12 || 12; // Convert 0 to 12
      return `${hours12}:${minutes.toString().padStart(2, "0")}${period}`;
    }

    // For 24-hour format
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  } catch (error) {
    console.warn("Error in formatTimeDisplay:", error);
    return timeStr; // Return original on error
  }
}

// Need a function that will get current time for a specific timezone
// This is important for accurate prayer time calculations and comparisons.
// Default to Europe/London if no timezone provided.
export function getCurrentTimeInTimezone(timezone?: string): Date {
  // Default to Europe/London if no timezone provided or invalid
  const tz =
    typeof timezone === "string" && timezone.trim()
      ? timezone
      : "Europe/London";
  const options: Intl.DateTimeFormatOptions = {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  };

  let parts: Intl.DateTimeFormatPart[];
  try {
    const formatter = new Intl.DateTimeFormat([], options);
    parts = formatter.formatToParts(new Date());
  } catch {
    // If the timezone is invalid, fallback to Europe/London
    const fallbackFormatter = new Intl.DateTimeFormat([], {
      ...options,
      timeZone: "Europe/London",
    });
    parts = fallbackFormatter.formatToParts(new Date());
  }

  const hour = parseInt(parts.find((p) => p.type === "hour")?.value || "0", 10);
  const minute = parseInt(
    parts.find((p) => p.type === "minute")?.value || "0",
    10,
  );
  const second = parseInt(
    parts.find((p) => p.type === "second")?.value || "0",
    10,
  );

  const now = new Date();
  now.setHours(hour, minute, second, 0);

  return now;
}

export function timeStringToDate(
  timeString: string,
  baseDate: Date = new Date(),
): Date {
  if (!timeString || timeString === "--:--") {
    return new Date(0); // Invalid date
  }

  const [hours, minutes] = timeString.split(":").map(Number);
  const date = new Date(baseDate);
  date.setHours(hours, minutes, 0, 0);
  return date;
}

/**
 * Formats a Hijri date object into a readable string
 * Handles both legacy and new nested structures
 *
 * Azure Best Practice: Centralized date formatting logic
 */
export function getFormattedHijriDate(
  hijriDate: any,
  hijriDateFormat?: string,
  customizedNames?: any,
): string {
  if (hijriDate && typeof hijriDate === "object") {
    // When format/customizedNames are provided and we have structured data, use formatHijriDate
    const day = hijriDate.day ?? hijriDate.Day;
    const monthName = hijriDate.monthName ?? hijriDate.MonthName;
    const year = hijriDate.year ?? hijriDate.Year;
    if (
      hijriDateFormat &&
      day !== undefined &&
      monthName &&
      year !== undefined
    ) {
      return formatHijriDate(
        String(day),
        String(monthName),
        String(year),
        hijriDateFormat,
        customizedNames,
      );
    }
    // Handle new nested structure with shortFormattedDate
    if ("shortFormattedDate" in hijriDate) {
      return hijriDate.shortFormattedDate;
    }
    // Handle legacy structure with FormattedDate
    if ("FormattedDate" in hijriDate) {
      return hijriDate.FormattedDate;
    }
    // Handle legacy structure with formattedDate
    if ("formattedDate" in hijriDate) {
      return hijriDate.formattedDate;
    }
  }
  return hijriDate ? String(hijriDate) : "";
}

/**
 * Determines the current Islamic date considering Maghrib transition
 *
 * Azure Best Practice: Centralized date calculation logic for consistent behavior
 *
 * @param prayerTimes - Today's prayer times
 * @param tomorrowPrayerTimes - Tomorrow's prayer times (optional)
 * @param changeAtMaghrib - Whether to change date at Maghrib (default: true)
 * @param timeZone - Timezone string (e.g., "Europe/London")
 * @returns Formatted Hijri date string
 */
export function getCurrentIslamicDate(
  prayerTimes: any | null,
  tomorrowPrayerTimes: any | null = null,
  changeAtMaghrib: boolean = true,
  timeZone: string = "UTC",
  hijriDateFormat?: string,
  customizedNames?: any,
): string {
  if (!prayerTimes) return "--";

  // Helper to get the best Hijri date source from a prayerTimes object
  const getHijriSource = (pt: any): any => {
    // Prefer HijriDateComponents (structured) when format params are provided
    if (hijriDateFormat && pt.HijriDateComponents) {
      return pt.HijriDateComponents;
    }
    return pt.HijriDate;
  };

  // If not changing at Maghrib, just return today's date
  if (!changeAtMaghrib) {
    return getFormattedHijriDate(
      getHijriSource(prayerTimes),
      hijriDateFormat,
      customizedNames,
    );
  }

  // Get current time in the specified timezone
  const now = getCurrentTimeInTimezone(timeZone);

  // Parse Maghrib time
  const maghribTime = parseTimeToDate(prayerTimes.JamMaghrib);

  // Calculate midnight boundaries
  const midnight = new Date(now);
  midnight.setHours(0, 0, 0, 0);
  const nextMidnight = new Date(midnight);
  nextMidnight.setDate(nextMidnight.getDate() + 1);

  // Check if we're after Maghrib but before midnight
  if (
    maghribTime &&
    now >= maghribTime &&
    now < nextMidnight &&
    tomorrowPrayerTimes &&
    tomorrowPrayerTimes.HijriDate
  ) {
    // Return tomorrow's Islamic date
    return getFormattedHijriDate(
      getHijriSource(tomorrowPrayerTimes),
      hijriDateFormat,
      customizedNames,
    );
  }

  // Return today's Islamic date
  return getFormattedHijriDate(
    getHijriSource(prayerTimes),
    hijriDateFormat,
    customizedNames,
  );
}

/**
 * Checks if current time is after Maghrib but before midnight
 *
 * Azure Best Practice: Helper function for Islamic date transition logic
 *
 * @param prayerTimes - Today's prayer times
 * @param timeZone - Timezone string
 * @returns true if currently in the post-Maghrib period
 */
export function isAfterMaghrib(
  prayerTimes: any | null,
  timeZone: string = "UTC",
): boolean {
  if (!prayerTimes) return false;

  const now = getCurrentTimeInTimezone(timeZone);
  const maghribTime = parseTimeToDate(prayerTimes.JamMaghrib);

  if (!maghribTime) return false;

  const midnight = new Date(now);
  midnight.setHours(0, 0, 0, 0);
  const nextMidnight = new Date(midnight);
  nextMidnight.setDate(nextMidnight.getDate() + 1);

  return now >= maghribTime && now < nextMidnight;
}
